import type { StageFormValues } from '../service-order-form';

import { Plus } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { Text } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import StageRow from './StageRow';

type Props = {
  stages: Array<StageFormValues>;
  onAddStage: () => void;
  onEditStage: (index: number) => void;
  footnote: string;
};

/**
 * The stages of a service order as rows with their dates, and the action to
 * add another.
 */
export default function ServiceOrderStagesCard({
  stages,
  onAddStage,
  onEditStage,
  footnote,
}: Props) {
  return (
    <html.div style={[Style.col, styles.card]}>
      <html.div style={[Style.row, Style.alignCenter, styles.head]}>
        <Text typeface="body.small.medium" color="neutral100" as="h2">
          Stages
        </Text>
        <Text typeface="caption.medium" color="neutral40">
          {stages.length}
        </Text>
        <html.span style={Style.fill} />
        <html.button
          style={[Style.row, Style.alignCenter, styles.add]}
          onClick={onAddStage}
        >
          <Plus size={14} weight="bold" color={color.accent} />
          <Text typeface="caption.semiBold" color="accent">
            Add stage
          </Text>
        </html.button>
      </html.div>

      <html.div style={[Style.row, Style.alignCenter, styles.columns]}>
        <Text
          typeface="caption.medium"
          color="neutral60"
          style={styles.colStage}
        >
          Stage
        </Text>
        <Text
          typeface="caption.medium"
          color="neutral60"
          style={styles.colDate}
        >
          Start
        </Text>
        <Text
          typeface="caption.medium"
          color="neutral60"
          style={styles.colDate}
        >
          Estimated finish
        </Text>
        <Text
          typeface="caption.medium"
          color="neutral60"
          style={styles.colDate}
        >
          Finish
        </Text>
        <html.span style={styles.colEdit} />
      </html.div>

      {stages.map((stage, index) => (
        <StageRow
          key={stage.key}
          stage={stage}
          onEdit={() => onEditStage(index)}
        />
      ))}

      <Text typeface="body.small.regular" color="neutral60">
        {footnote}
      </Text>
    </html.div>
  );
}

const styles = css.create({
  card: {
    gap: 12,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    backgroundColor: color.neutral0,
  },
  head: {
    gap: 6,
  },
  add: {
    gap: 4,
    padding: 0,
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  columns: {
    gap: 8,
    display: {
      default: 'flex',
      '@media (max-width: 640px)': 'none',
    },
  },
  colStage: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  colDate: {
    flexShrink: 0,
    width: 140,
  },
  colEdit: {
    flexShrink: 0,
    width: 20,
  },
});
